import { CircleCheck, DollarSign, DownloadIcon, ExternalLink, FileText, MoreVertical, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { PanelDePagoDelAbono } from "@/components/informes/PanelDePagoDelAbono";
import { puedeCotizar, puedeMarcarAceptada } from "@/components/informes/acciones";
import { estadoDelAbono } from "@/components/informes/formato";
import { BarraDeEtapa } from "@/components/ui/barra-de-etapa";
import { Button } from "@/components/ui/button";
import { ChipPendiente } from "@/components/ui/chip-pendiente";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PilaDeAvatares } from "@/components/ui/pila-de-avatares";
import { Spinner } from "@/components/ui/spinner";
import { TableCell, TableRow } from "@/components/ui/table";
import { useDescargarCotizacion } from "@/hooks/useDescargarCotizacion";
import { formatearFecha } from "@/lib/fechas";
import {
  DESCRIPCION_ESTADO,
  ETAPAS_DEL_INFORME,
  ETIQUETA_CORTA_ESTADO_INFORME,
  ROLES_DEL_EQUIPO,
  type InformeEnProceso,
  type ResumenDelInforme,
} from "@/lib/informes";
import { PARAM_ORIGEN, urlDeLaEdicion } from "@/lib/origenDeNavegacion";
import { describirEntregaRelativa, haceCuanto, type TonoDeEntrega } from "@/lib/tiempoRelativo";
import { cn } from "@/lib/utils";

const TONO_DE_ENTREGA: Record<TonoDeEntrega, string> = {
  danger: "text-danger-texto",
  warning: "text-warning-texto",
  medio: "text-ink-medio",
  success: "text-success-soft-foreground",
};

const ORIGEN_DE_LA_ENTREGA = {
  estimada: "Fecha estimada por el ejecutivo",
  comprometida: "Fecha comprometida con el cliente",
} as const;

/** Un clic dentro de un control de la fila NO abre el informe. */
const detener = (evento: React.SyntheticEvent) => evento.stopPropagation();

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`;
}

interface FilaDeInformeEnProcesoProps {
  informe: InformeEnProceso;
  /** El panel de pago en línea está abierto bajo esta fila. */
  pagoAbierto: boolean;
  onAbrirPago: () => void;
  onCerrarPago: () => void;
  onVerContacto: () => void;
  onMarcarAceptada: () => void;
}

/**
 * Una fila de "Informes en proceso": predio y N°, cliente, etapa, pendientes,
 * entrega, equipo y menú. Toda la fila abre el informe; el nombre del predio es
 * además un enlace real (teclado, clic con rueda) y los controles de adentro
 * detienen el clic para no abrirlo.
 *
 * Una fila con el anticipo por cobrar lleva un tinte cálido y una franja de 3px
 * a la izquierda: es lo primero que esta cola quiere hacer visible.
 */
export function FilaDeInformeEnProceso({
  informe,
  pagoAbierto,
  onAbrirPago,
  onCerrarPago,
  onVerContacto,
  onMarcarAceptada,
}: FilaDeInformeEnProcesoProps) {
  const navegar = useNavigate();
  const { resumen } = informe;
  const abono = informe.cotizacion
    ? estadoDelAbono(informe.cotizacion.requiere_abono, informe.cotizacion.abono_pagado_en)
    : null;
  const anticipoPendiente = abono === "pendiente";
  const abrir = urlDeLaEdicion(informe.id, "informes");
  const numero = informe.numero_informe?.trim() || "sin número";

  return (
    <>
      <TableRow
        data-anticipo={anticipoPendiente ? "pendiente" : undefined}
        onClick={() => navegar(abrir)}
        className={cn(
          "hover:bg-superficie-tenue h-16 cursor-pointer",
          anticipoPendiente && "bg-fila-anticipo shadow-[inset_3px_0_0_var(--warning-dot)]",
          pagoAbierto && "bg-superficie-tenue"
        )}
      >
        <TableCell className="overflow-hidden py-1.5 pl-[1.125rem]">
          <Link
            to={abrir}
            onClick={detener}
            title={informe.propiedad_nombre ?? undefined}
            className="text-ink block truncate text-[0.84375rem] font-bold hover:underline"
          >
            {informe.propiedad_nombre?.trim() || "—"}
          </Link>
          <div className="mt-0.5 flex items-center gap-[0.4375rem]">
            <span className="text-ink-secundario bg-divisor rounded-[0.3125rem] px-1.5 font-mono text-[0.71875rem] font-bold">
              {numero}
            </span>
            {anticipoPendiente && (
              <button
                type="button"
                title="El cliente aún no paga el anticipo · registrar pago"
                onClick={(evento) => {
                  detener(evento);
                  onAbrirPago();
                }}
                className="bg-warning-dot focus-visible:ring-ring/50 inline-flex h-[1.125rem] shrink-0 items-center gap-1 rounded-[0.3125rem] px-1.5 text-[0.625rem] font-extrabold text-white outline-none focus-visible:ring-[3px]"
              >
                <DollarSign aria-hidden className="size-[0.6875rem]" strokeWidth={2.6} />
                Sin anticipo
              </button>
            )}
            <Documentos resumen={resumen} />
            <span className="text-ink-hint truncate text-[0.71875rem] font-semibold">
              · {haceCuanto(resumen.ultima_actividad)}
            </span>
          </div>
        </TableCell>

        <TableCell className="overflow-hidden">
          <span
            title={informe.cliente_nombre ?? undefined}
            className="text-ink-body block truncate text-[0.78125rem] font-semibold"
          >
            {informe.cliente_nombre || "—"}
          </span>
        </TableCell>

        <TableCell>
          <Etapa informe={informe} />
        </TableCell>

        <TableCell onClick={detener} className="whitespace-normal">
          <Pendientes informe={informe} />
        </TableCell>

        <TableCell>
          <Entrega resumen={resumen} />
        </TableCell>

        <TableCell onClick={detener}>
          <PilaDeAvatares
            nombre="Equipo del informe"
            equipo={ROLES_DEL_EQUIPO.map(({ clave, etiqueta }) => ({
              rol: etiqueta,
              persona: resumen.equipo[clave],
              leToca: resumen.le_toca === clave,
            }))}
          />
        </TableCell>

        {/* Los ítems del menú viven en un portal, pero React burbujea los
            eventos por el ÁRBOL de componentes: sin detener acá, elegir una
            acción también abriría el informe. */}
        <TableCell onClick={detener} className="pr-[1.125rem] text-right">
          <MenuDeLaFila
            informe={informe}
            abono={abono}
            onVerContacto={onVerContacto}
            onMarcarAceptada={onMarcarAceptada}
            onAbrirPago={onAbrirPago}
          />
        </TableCell>
      </TableRow>

      {pagoAbierto && informe.cotizacion && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={7} className="p-0">
            <PanelDePagoDelAbono
              informeId={informe.id}
              numero={numero}
              cotizacion={informe.cotizacion}
              onCerrar={onCerrarPago}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function Documentos({ resumen }: { resumen: ResumenDelInforme }) {
  const { documentos, documentos_por_clasificar: porClasificar } = resumen;
  const titulo =
    `${plural(documentos, "documento subido", "documentos subidos")}` +
    (porClasificar ? ` · ${porClasificar} del cliente por clasificar` : "");

  return (
    <span
      title={titulo}
      className="text-ink-muted inline-flex shrink-0 items-center gap-1 text-[0.71875rem] font-bold"
    >
      <FileText aria-hidden className="size-3" />
      <span className="font-mono">{documentos}</span>
      {porClasificar > 0 && (
        <span className="bg-warning-soft text-warning-texto rounded-[0.25rem] px-1 font-mono">
          +{porClasificar}
        </span>
      )}
    </span>
  );
}

function Etapa({ informe }: { informe: InformeEnProceso }) {
  const { indice } = informe.resumen.etapa;
  // La primera etapa agrupa los estados de la cotización: dice el corto
  // ("Enviada", "En visado") para que ese matiz no se pierda.
  const primera = indice === 0;

  return (
    <BarraDeEtapa
      etapas={ETAPAS_DEL_INFORME}
      indice={indice}
      etiqueta={primera ? ETIQUETA_CORTA_ESTADO_INFORME[informe.estado] : undefined}
      descripcion={primera ? DESCRIPCION_ESTADO[informe.estado] : undefined}
    />
  );
}

function Pendientes({ informe }: { informe: InformeEnProceso }) {
  const navegar = useNavigate();
  const { resumen } = informe;
  const { entrega } = resumen;
  const atrasados = entrega.atrasado && entrega.dias !== null ? -entrega.dias : 0;

  const hayAlgo = atrasados > 0 || resumen.novedades_app > 0 || resumen.dudas_ia > 0;
  if (!hayAlgo) return <span className="text-ink-hint text-xs font-semibold">Al día</span>;

  return (
    <div className="flex flex-wrap gap-[0.3125rem]">
      {atrasados > 0 && (
        <ChipPendiente
          tono="danger"
          title={entrega.fecha ? `La entrega era el ${formatearFecha(entrega.fecha)}` : undefined}
          onClick={() => navegar(urlDeLaEdicion(informe.id, "informes"))}
        >
          Atrasado {plural(atrasados, "día", "días")}
        </ChipPendiente>
      )}
      {resumen.novedades_app > 0 && (
        <ChipPendiente
          tono="info"
          title={`${plural(resumen.novedades_app, "sincronización", "sincronizaciones")} desde la app de terreno sin revisar`}
          onClick={() => navegar(`/informes/${informe.id}/terreno?${PARAM_ORIGEN}=informes`)}
        >
          App · {plural(resumen.novedades_app, "actualización", "actualizaciones")}
        </ChipPendiente>
      )}
      {resumen.dudas_ia > 0 && (
        <ChipPendiente
          tono="warning"
          title="Abrir la generación del informe"
          onClick={() => navegar(`/informes/${informe.id}/generacion`)}
        >
          {plural(resumen.dudas_ia, "duda de la IA", "dudas de la IA")}
        </ChipPendiente>
      )}
    </div>
  );
}

/**
 * La entrega: la fecha que ya está escrita (nunca una calculada desde el plazo,
 * que corre desde la visita a terreno) y cuánto falta o cuánto pasó. Sin fecha,
 * lo único honesto es el plazo pactado.
 */
function Entrega({ resumen }: { resumen: ResumenDelInforme }) {
  const { entrega } = resumen;
  const enviado = resumen.etapa.indice >= ETAPAS_DEL_INFORME.length - 1;
  const relativa = describirEntregaRelativa(entrega.dias, enviado);

  if (!entrega.fecha) {
    return (
      <div>
        <div className="text-ink-hint font-mono text-[0.8125rem] font-bold">—</div>
        {relativa ? (
          <div className={cn("text-[0.71875rem] font-bold", TONO_DE_ENTREGA[relativa.tono])}>
            {relativa.texto}
          </div>
        ) : (
          entrega.plazo_dias_habiles !== null && (
            <div className="text-ink-medio text-[0.71875rem] font-semibold whitespace-normal">
              {plural(entrega.plazo_dias_habiles, "día hábil", "días hábiles")} desde la visita
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        title={entrega.origen ? ORIGEN_DE_LA_ENTREGA[entrega.origen] : undefined}
        className="text-ink font-mono text-[0.8125rem] font-bold"
      >
        {formatearFecha(entrega.fecha)}
      </div>
      {relativa && (
        <div className={cn("mt-px text-[0.71875rem] font-bold", TONO_DE_ENTREGA[relativa.tono])}>
          {relativa.texto}
        </div>
      )}
    </div>
  );
}

/**
 * `modal={false}`: dos acciones abren una modal, y un menú modal que se cierra
 * mientras la modal se abre puede dejar el `body` con `pointer-events: none`.
 */
function MenuDeLaFila({
  informe,
  abono,
  onVerContacto,
  onMarcarAceptada,
  onAbrirPago,
}: {
  informe: InformeEnProceso;
  abono: ReturnType<typeof estadoDelAbono> | null;
  onVerContacto: () => void;
  onMarcarAceptada: () => void;
  onAbrirPago: () => void;
}) {
  const { descargando, descargar } = useDescargarCotizacion(informe.id);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Más acciones" className="rounded-[0.625rem]">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuItem asChild>
          <Link to={urlDeLaEdicion(informe.id, "informes")}>
            <ExternalLink />
            Abrir informe
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={onVerContacto}>
          <Phone />
          Ver contacto del cliente
        </DropdownMenuItem>

        {/* "Corregir" y no "Editar": lo que se arregla es un error de captura. */}
        {(abono === "pendiente" || abono === "pagado") && (
          <DropdownMenuItem onSelect={onAbrirPago}>
            <DollarSign />
            {abono === "pagado" ? "Corregir pago del anticipo" : "Registrar pago del anticipo"}
          </DropdownMenuItem>
        )}

        {/* Abre la modal y no acepta en un clic: mueve el informe a producción,
            le abre el portal al cliente y le manda un correo. */}
        {puedeMarcarAceptada(informe.estado) && (
          <DropdownMenuItem onSelect={onMarcarAceptada}>
            <CircleCheck />
            Marcar aceptada
          </DropdownMenuItem>
        )}

        {informe.cotizacion && (
          <DropdownMenuItem disabled={descargando} onSelect={() => void descargar()}>
            {descargando ? <Spinner /> : <DownloadIcon />}
            Descargar cotización
          </DropdownMenuItem>
        )}

        {puedeCotizar(informe.estado) ? (
          <DropdownMenuItem asChild>
            <Link to={`/informes/${informe.id}/cotizacion`}>
              <FileText />
              {informe.cotizacion ? "Recotizar" : "Cotizar"}
            </Link>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled title="Este informe no se puede cotizar en su estado actual.">
            <FileText />
            Cotizar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
